import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator, Pressable, TextInput } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { Gift, TrendingUp, Users, DollarSign, Search } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';
import { formatCentsToUSDShort, REFERRAL_CONFIG } from '../../config/referral';
import { SUPABASE_URL } from '../../lib/constants';

const REFERRER_CREDIT_CENTS = REFERRAL_CONFIG.REFERRER_CREDIT_CENTS;
const NEW_MEMBER_CREDIT_CENTS = REFERRAL_CONFIG.NEW_MEMBER_CREDIT_CENTS;

interface ReferralStats {
  totalInvitationsSent: number;
  totalInvitationsClaimed: number;
  conversionRate: number;
  totalCreditsIssued: number;
  referrerCredits: number;
  newMemberCredits: number;
  averageCreditsPerUser: number;
}

interface ReferralInvitation {
  id: string;
  invitation_code: string;
  referrer_id: string;
  recipient_email: string | null;
  recipient_name: string | null;
  status: string;
  created_at: string;
  claimed_at: string | null;
  claimed_by: string | null;
  profiles: {
    full_name: string;
  };
}

interface TopReferrer {
  referrer_id: string;
  referrer_name: string;
  total_invitations: number;
  claimed_invitations: number;
}

const ReferralsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ReferralStats>({
    totalInvitationsSent: 0,
    totalInvitationsClaimed: 0,
    conversionRate: 0,
    totalCreditsIssued: 0,
    referrerCredits: 0,
    newMemberCredits: 0,
    averageCreditsPerUser: 0,
  });
  const [invitations, setInvitations] = useState<ReferralInvitation[]>([]);
  const [topReferrers, setTopReferrers] = useState<TopReferrer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // User limit management
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newLimit, setNewLimit] = useState('');
  const [updatingLimit, setUpdatingLimit] = useState(false);

  // Manual credit granting
  const [creditSearchEmail, setCreditSearchEmail] = useState('');
  const [selectedCreditUser, setSelectedCreditUser] = useState<any>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [grantingCredit, setGrantingCredit] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditSuccess, setCreditSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all invitations
      let invitationsQuery = supabase
        .from('referral_invitations')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        invitationsQuery = invitationsQuery.eq('status', statusFilter);
      }

      const { data: invitationsData, error: invitationsError } = await invitationsQuery;

      if (invitationsError) throw invitationsError;

      // Fetch profiles for all referrers
      const referrerIds = [...new Set(invitationsData?.map(inv => inv.referrer_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', referrerIds);

      // Create a map for quick lookup
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      // Attach profile data to invitations
      const invitationsWithProfiles = invitationsData?.map(inv => ({
        ...inv,
        profiles: profilesMap.get(inv.referrer_id) ? {
          full_name: profilesMap.get(inv.referrer_id)!.full_name
        } : null
      })) || [];

      // Fetch all credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('referral_credits')
        .select('*');

      if (creditsError) throw creditsError;

      // Calculate stats (all invitations in DB are now "claimed" since we only create on claim)
      const totalClaimed = invitationsWithProfiles?.length || 0;
      const conversionRate = 100; // Since we only track successful referrals now

      const referrerCreditsTotal = creditsData?.filter(c => c.credit_type === 'referrer_bonus')
        .reduce((sum, c) => sum + c.amount_cents, 0) || 0;
      
      const newMemberCreditsTotal = creditsData?.filter(c => c.credit_type === 'new_member_bonus')
        .reduce((sum, c) => sum + c.amount_cents, 0) || 0;

      const manualCreditsTotal = creditsData?.filter(c => c.credit_type === 'manual_adjustment')
        .reduce((sum, c) => sum + c.amount_cents, 0) || 0;

      const totalCredits = referrerCreditsTotal + newMemberCreditsTotal + manualCreditsTotal;

      // Get unique users with credits
      const uniqueUsers = new Set(creditsData?.map(c => c.user_id) || []);
      const avgCreditsPerUser = uniqueUsers.size > 0 ? totalCredits / uniqueUsers.size : 0;

      // Calculate top referrers
      const referrerMap = new Map<string, { name: string; total: number }>();
      
      invitationsWithProfiles?.forEach(inv => {
        const referrerId = inv.referrer_id;
        const referrerName = inv.profiles?.full_name || 'Unknown';
        
        if (!referrerMap.has(referrerId)) {
          referrerMap.set(referrerId, { name: referrerName, total: 0 });
        }
        
        const referrerStats = referrerMap.get(referrerId)!;
        referrerStats.total++;
      });

      const topReferrersList: TopReferrer[] = Array.from(referrerMap.entries())
        .map(([id, stats]) => ({
          referrer_id: id,
          referrer_name: stats.name,
          total_invitations: stats.total,
          claimed_invitations: stats.total, // All invitations are claimed now
        }))
        .sort((a, b) => b.claimed_invitations - a.claimed_invitations)
        .slice(0, 5);

      setStats({
        totalInvitationsSent: totalClaimed,
        totalInvitationsClaimed: totalClaimed,
        conversionRate,
        totalCreditsIssued: totalCredits,
        referrerCredits: referrerCreditsTotal,
        newMemberCredits: newMemberCreditsTotal,
        averageCreditsPerUser: avgCreditsPerUser,
      });

      setInvitations(invitationsWithProfiles);
      setTopReferrers(topReferrersList);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUser = async () => {
    if (!searchEmail.trim()) return;

    try {
      const searchQuery = searchEmail.trim();
      
      // Check if search query looks like a UUID (36 characters with hyphens)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchQuery);
      
      let query = supabase
        .from('profiles')
        .select('id, full_name, referral_invitations_remaining')
        .limit(1);

      if (isUUID) {
        // Search by exact UUID match
        query = query.eq('id', searchQuery);
      } else {
        // Search by name (case-insensitive partial match)
        query = query.ilike('full_name', `%${searchQuery}%`);
      }

      const { data, error } = await query.single();

      if (error) {
        // If single() fails, try without single() to get first result
        let retryQuery = supabase
          .from('profiles')
          .select('id, full_name, referral_invitations_remaining')
          .limit(1);
        
        if (isUUID) {
          retryQuery = retryQuery.eq('id', searchQuery);
        } else {
          retryQuery = retryQuery.ilike('full_name', `%${searchQuery}%`);
        }
        
        const { data: results, error: resultsError } = await retryQuery;
        
        if (!resultsError && results && results.length > 0) {
          const profile = results[0];
          setSelectedUser(profile);
          setNewLimit(profile.referral_invitations_remaining.toString());
        } else {
          console.error('User not found:', resultsError || error);
          alert('User not found. Try searching by name or user ID.');
        }
      } else if (data) {
        setSelectedUser(data);
        setNewLimit(data.referral_invitations_remaining.toString());
      }
    } catch (error) {
      console.error('Error searching user:', error);
      alert('Error searching for user. Please try again.');
    }
  };

  const updateUserLimit = async () => {
    if (!selectedUser || !newLimit) return;

    try {
      setUpdatingLimit(true);
      const limitValue = parseInt(newLimit, 10);

      if (isNaN(limitValue) || limitValue < 0) {
        alert('Please enter a valid number');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ referral_invitations_remaining: limitValue })
        .eq('id', selectedUser.id);

      if (error) throw error;

      alert('Invitation limit updated successfully');
      setSelectedUser({ ...selectedUser, referral_invitations_remaining: limitValue });
    } catch (error) {
      console.error('Error updating limit:', error);
      alert('Failed to update invitation limit');
    } finally {
      setUpdatingLimit(false);
    }
  };

  const searchCreditUser = async () => {
    if (!creditSearchEmail.trim()) return;

    try {
      setCreditError(null);
      setCreditSuccess(null);
      const searchQuery = creditSearchEmail.trim();
      
      // Check if search query looks like a UUID (36 characters with hyphens)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchQuery);
      
      let query = supabase
        .from('profiles')
        .select('id, full_name, referral_credit_balance_cents')
        .limit(1);

      if (isUUID) {
        // Search by exact UUID match
        query = query.eq('id', searchQuery);
      } else {
        // Search by name (case-insensitive partial match)
        query = query.ilike('full_name', `%${searchQuery}%`);
      }

      const { data, error } = await query.single();

      if (error) {
        // If single() fails, try without single() to get first result
        let retryQuery = supabase
          .from('profiles')
          .select('id, full_name, referral_credit_balance_cents')
          .limit(1);
        
        if (isUUID) {
          retryQuery = retryQuery.eq('id', searchQuery);
        } else {
          retryQuery = retryQuery.ilike('full_name', `%${searchQuery}%`);
        }
        
        const { data: results, error: resultsError } = await retryQuery;
        
        if (!resultsError && results && results.length > 0) {
          const profile = results[0];
          setSelectedCreditUser(profile);
          setCreditAmount('');
          setCreditReason('');
        } else {
          console.error('User not found:', resultsError || error);
          setCreditError('User not found. Try searching by name or user ID.');
        }
      } else if (data) {
        setSelectedCreditUser(data);
        setCreditAmount('');
        setCreditReason('');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setCreditError('Error searching for user. Please try again.');
    }
  };

  const grantManualCredit = async () => {
    if (!selectedCreditUser || !creditAmount) {
      setCreditError('Please enter an amount');
      return;
    }

    try {
      setGrantingCredit(true);
      setCreditError(null);
      setCreditSuccess(null);

      const amountValue = parseFloat(creditAmount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setCreditError('Please enter a valid amount greater than 0');
        return;
      }

      const amountCents = Math.round(amountValue * 100);

      // Get current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCreditError('Not authenticated');
        return;
      }

      // Call the edge function
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/grant-manual-credit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            user_id: selectedCreditUser.id,
            amount_cents: amountCents,
            reason: creditReason.trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to grant credit');
      }

      const result = await response.json();
      
      setCreditSuccess(
        `Credit of $${amountValue.toFixed(2)} granted successfully${result.credit.stripe_applied ? ' and applied to Stripe' : ' (pending Stripe application)'}`
      );
      
      // Reset form
      setCreditAmount('');
      setCreditReason('');
      
      // Refresh user data to show updated balance
      const { data: updatedUser } = await supabase
        .from('profiles')
        .select('id, full_name, referral_credit_balance_cents')
        .eq('id', selectedCreditUser.id)
        .single();
      
      if (updatedUser) {
        setSelectedCreditUser(updatedUser);
      }

      // Refresh overall stats
      await fetchData();

      // Clear success message after 5 seconds
      setTimeout(() => setCreditSuccess(null), 5000);
    } catch (error: any) {
      console.error('Error granting credit:', error);
      setCreditError(error.message || 'Failed to grant credit');
    } finally {
      setGrantingCredit(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'claimed':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'expired':
        return '#ef4444';
      case 'cancelled':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const filteredInvitations = invitations.filter(inv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      inv.invitation_code.toLowerCase().includes(query) ||
      inv.profiles?.full_name?.toLowerCase().includes(query) ||
      inv.recipient_email?.toLowerCase().includes(query) ||
      inv.recipient_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#7c3aed" />
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>Referral Program</Text>
          <Text style={styles.subtitle}>Track invitation stats and manage user limits</Text>
        </View>

        {/* Stats Grid */}
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Gift size={20} color="#7c3aed" />
            <Text style={styles.statValue}>{stats.totalInvitationsClaimed}</Text>
            <Text style={styles.statLabel}>People Joined</Text>
          </View>
          <View style={styles.statCard}>
            <Users size={20} color="#3b82f6" />
            <Text style={styles.statValue}>{topReferrers.length}</Text>
            <Text style={styles.statLabel}>Active Referrers</Text>
          </View>
          <View style={styles.statCard}>
            <DollarSign size={20} color="#f59e0b" />
            <Text style={styles.statValue}>{formatCentsToUSDShort(stats.totalCreditsIssued)}</Text>
            <Text style={styles.statLabel}>Total Credits</Text>
          </View>
          <View style={styles.statCard}>
            <TrendingUp size={20} color="#10b981" />
            <Text style={styles.statValue}>{formatCentsToUSDShort(Math.round(stats.averageCreditsPerUser))}</Text>
            <Text style={styles.statLabel}>Avg per User</Text>
          </View>
        </View>

        {/* Top Referrers */}
        {topReferrers.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Top Referrers</Text>
            <View style={styles.card}>
              {topReferrers.map((referrer, index) => (
                <View key={referrer.referrer_id} style={styles.topReferrerRow}>
                  <View style={styles.topReferrerRank}>
                    <Text style={styles.topReferrerRankText}>#{index + 1}</Text>
                  </View>
                  <View style={styles.topReferrerInfo}>
                    <Text style={styles.topReferrerName}>{referrer.referrer_name}</Text>
                    <Text style={styles.topReferrerStats}>
                      {referrer.claimed_invitations} {referrer.claimed_invitations === 1 ? 'person' : 'people'} joined
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Manage User Limits */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Manage Invitation Limits</Text>
          <View style={styles.card}>
            <View style={styles.searchRow}>
              <TextInput
                style={styles.searchInput}
                placeholder="Search by user ID or email"
                value={searchEmail}
                onChangeText={setSearchEmail}
              />
              <Pressable style={styles.searchButton} onPress={searchUser}>
                <Search size={18} color="#ffffff" />
                <Text style={styles.searchButtonText}>Search</Text>
              </Pressable>
            </View>

            {selectedUser && (
              <View style={styles.userLimitSection}>
                <Text style={styles.userLimitName}>{selectedUser.full_name || selectedUser.email}</Text>
                <Text style={styles.userLimitCurrent}>
                  Current limit: {selectedUser.referral_invitations_remaining}
                </Text>
                <View style={styles.limitUpdateRow}>
                  <TextInput
                    style={styles.limitInput}
                    placeholder="New limit"
                    value={newLimit}
                    onChangeText={setNewLimit}
                    keyboardType="number-pad"
                  />
                  <Pressable
                    style={[styles.updateButton, updatingLimit && styles.buttonDisabled]}
                    onPress={updateUserLimit}
                    disabled={updatingLimit}
                  >
                    {updatingLimit ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.updateButtonText}>Update</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Grant Manual Credits */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Grant Manual Credits</Text>
          <View style={styles.card}>
            <View style={styles.searchRow}>
              <TextInput
                style={styles.searchInput}
                placeholder="Search by user ID or name"
                value={creditSearchEmail}
                onChangeText={setCreditSearchEmail}
              />
              <Pressable style={styles.searchButton} onPress={searchCreditUser}>
                <Search size={18} color="#ffffff" />
                <Text style={styles.searchButtonText}>Search</Text>
              </Pressable>
            </View>

            {selectedCreditUser && (
              <View style={styles.userLimitSection}>
                <Text style={styles.userLimitName}>
                  {selectedCreditUser.full_name || 'Unknown User'}
                </Text>
                <Text style={styles.userLimitCurrent}>
                  Current balance: {formatCentsToUSDShort(selectedCreditUser.referral_credit_balance_cents || 0)}
                </Text>
                
                {creditError && (
                  <View style={styles.errorMessage}>
                    <Text style={styles.errorText}>{creditError}</Text>
                  </View>
                )}
                
                {creditSuccess && (
                  <View style={styles.successMessage}>
                    <Text style={styles.successText}>{creditSuccess}</Text>
                  </View>
                )}

                <View style={styles.creditForm}>
                  <View style={styles.creditInputRow}>
                    <Text style={styles.creditLabel}>Amount ($)</Text>
                    <TextInput
                      style={styles.creditAmountInput}
                      placeholder="0.00"
                      value={creditAmount}
                      onChangeText={setCreditAmount}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  
                  <View style={styles.creditInputRow}>
                    <Text style={styles.creditLabel}>Reason (optional)</Text>
                    <TextInput
                      style={styles.creditReasonInput}
                      placeholder="e.g., Customer service adjustment"
                      value={creditReason}
                      onChangeText={setCreditReason}
                      multiline
                      numberOfLines={3}
                    />
                  </View>

                  <Pressable
                    style={[styles.updateButton, grantingCredit && styles.buttonDisabled]}
                    onPress={grantManualCredit}
                    disabled={grantingCredit}
                  >
                    {grantingCredit ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.updateButtonText}>Grant Credit</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* All Referrals Table */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>All Successful Referrals</Text>
            <View style={styles.filterRow}>
              <TextInput
                style={styles.filterInput}
                placeholder="Search..."
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>
          </View>

          <View style={styles.card}>
            {filteredInvitations.length === 0 ? (
              <Text style={styles.emptyText}>No referrals found</Text>
            ) : (
              <View style={styles.table}>
                {filteredInvitations.map((invitation) => (
                  <View key={invitation.id} style={styles.tableRow}>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Code Used</Text>
                      <Text style={styles.tableCellValue}>{invitation.invitation_code}</Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Referrer</Text>
                      <Text style={styles.tableCellValue}>
                        {invitation.profiles?.full_name || 'Unknown'}
                      </Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Joined Date</Text>
                      <Text style={styles.tableCellValue}>
                        {new Date(invitation.claimed_at!).toLocaleDateString()}
                      </Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Credits Issued</Text>
                      <Text style={styles.tableCellValue}>
                        ${((REFERRER_CREDIT_CENTS + NEW_MEMBER_CREDIT_CENTS) / 100).toFixed(0)}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  content: {
    padding: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    flex: 1,
    minWidth: 200,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
    marginTop: 12,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  section: {
    marginBottom: 32,
  },
  sectionHeader: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  filterRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 12,
  },
  filterInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  statusFilter: {
    flexDirection: 'row',
    gap: 8,
  },
  filterButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  filterButtonActive: {
    backgroundColor: '#7c3aed',
    borderColor: '#7c3aed',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: '#ffffff',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  topReferrerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  topReferrerRank: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#7c3aed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  topReferrerRankText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  topReferrerInfo: {
    flex: 1,
  },
  topReferrerName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 2,
  },
  topReferrerStats: {
    fontSize: 13,
    color: '#6b7280',
  },
  searchRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  searchInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
  },
  searchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#7c3aed',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  searchButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  userLimitSection: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    paddingTop: 20,
  },
  userLimitName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  userLimitCurrent: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 12,
  },
  limitUpdateRow: {
    flexDirection: 'row',
    gap: 12,
  },
  limitInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
  },
  updateButton: {
    backgroundColor: '#7c3aed',
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 8,
    justifyContent: 'center',
    minWidth: 100,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  updateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    paddingVertical: 32,
  },
  table: {
    gap: 12,
  },
  tableRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableCell: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 120 : undefined,
  },
  tableCellLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  tableCellValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  creditForm: {
    marginTop: 12,
  },
  creditInputRow: {
    marginBottom: 16,
  },
  creditLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 8,
  },
  creditAmountInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  creditReasonInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 14,
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorMessage: {
    backgroundColor: '#fee2e2',
    borderWidth: 1,
    borderColor: '#fca5a5',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    marginBottom: 12,
  },
  errorText: {
    fontSize: 14,
    color: '#dc2626',
  },
  successMessage: {
    backgroundColor: '#d1fae5',
    borderWidth: 1,
    borderColor: '#86efac',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    marginBottom: 12,
  },
  successText: {
    fontSize: 14,
    color: '#16a34a',
  },
});

export default ReferralsTab;
